// src/components/pmt3/dashboards/AsinResultsByMonthDashboard.tsx

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useAsinMonthlyResults } from '@/hooks/useAsinMonthlyResults';
import { MetricConfig, MonthlyMetric } from '@/types/asinMonthlyResultsTypes';
import { formatValue } from '@/utils/asinMonthlyResultsFormatters';

/**
 * Configuration for all 18 metrics with their display labels and formatting rules
 */
const METRIC_CONFIGS: MetricConfig[] = [
  { key: 'totalTraffic', label: 'Total Traffic', formatter: 'wholeNumber' },
  { key: 'totalUnits', label: 'Total Units', formatter: 'wholeNumber' },
  { key: 'totalRevenue', label: 'Total Revenue', formatter: 'currency' },
  { key: 'totalGrossProfit', label: 'Total Gross Profit', formatter: 'currency' },
  { key: 'grossProfitPerDay', label: 'Gross Profit Per Day', formatter: 'currency' },
  { key: 'grossProfitMargin', label: 'Gross Profit Margin', formatter: 'currency' },
  { key: 'overallConversionRate', label: 'Overall Conversion Rate', formatter: 'percentage' },
  { key: 'ppcImpressions', label: 'PPC Impressions', formatter: 'wholeNumber' },
  { key: 'ppcClicks', label: 'PPC Clicks', formatter: 'wholeNumber' },
  { key: 'ppcSales', label: 'PPC Sales', formatter: 'currency' },
  { key: 'ppcSpend', label: 'PPC Spend', formatter: 'currency' },
  { key: 'ppcOrders', label: 'PPC Orders', formatter: 'wholeNumber' },
  { key: 'ppcUnits', label: 'PPC Units', formatter: 'wholeNumber' },
  { key: 'ppcConversionRate', label: 'PPC Conversion Rate', formatter: 'percentage' },
  { key: 'ppcClickThroughRate', label: 'PPC Click Through Rate', formatter: 'percentage' },
  { key: 'ppcCostPerClick', label: 'PPC Cost Per Click', formatter: 'currencyWithCents' },
  { key: 'acos', label: 'ACoS', formatter: 'percentage' },
  { key: 'tacos', label: 'TACOS', formatter: 'percentage' },
];

/**
 * Component for rendering individual metric cells with proper formatting
 */
interface MetricCellProps {
  value: number | undefined;
  formatter: MetricConfig['formatter'];
  isLoading: boolean;
}

const MetricCell: React.FC<MetricCellProps> = ({ value, formatter, isLoading }) => {
  if (isLoading) {
    return <Skeleton className="h-4 w-16" />;
  }

  const formattedValue = formatValue(value, formatter);
  
  return (
    <TableCell className="text-right py-2 px-3 whitespace-nowrap">
      {formattedValue}
    </TableCell>
  );
};

/**
 * Main dashboard component for displaying ASIN results by month
 */
const AsinResultsByMonthDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = useAsinMonthlyResults(
    selectedCompany,
    selectedProduct?.id
  );

  // Separate TTM data from monthly data and sort months chronologically
  const { ttmColumns, sortedMonths } = useMemo(() => {
    if (!data?.monthlyMetrics) return { ttmColumns: [], sortedMonths: [] };
    
    const monthlyMetrics = data.monthlyMetrics;
    const ttmKeys = ['TTM Total', 'TTM Avg'];
    const monthKeys = Object.keys(monthlyMetrics).filter(key => !ttmKeys.includes(key));
    
    // Sort months chronologically (most recent first)
    const sorted = monthKeys.sort((a, b) => {
      // Parse month strings like "May 2025" for sorting
      const parseMonth = (monthStr: string) => {
        const [month, year] = monthStr.split(' ');
        const monthIndex = new Date(`${month} 1, ${year}`).getMonth();
        return new Date(parseInt(year), monthIndex);
      };
      
      return parseMonth(b).getTime() - parseMonth(a).getTime();
    });
    
    return {
      ttmColumns: ttmKeys.filter(key => monthlyMetrics[key]), // Only include TTM columns that exist
      sortedMonths: sorted
    };
  }, [data]);

  // Helper function to abbreviate month names like in the spreadsheet
  const abbreviateMonth = (monthStr: string) => {
    if (!monthStr || typeof monthStr !== 'string') {
      return monthStr; // Return as-is if invalid
    }
    
    const parts = monthStr.split(' ');
    if (parts.length !== 2) {
      return monthStr; // Return as-is if not in expected format
    }
    
    const [month, year] = parts;
    if (!month || !year) {
      return monthStr; // Return as-is if either part is missing
    }
    
    const monthAbbreviations: { [key: string]: string } = {
      'January': 'Jan.',
      'February': 'Feb.',
      'March': 'Mar.',
      'April': 'Apr.',
      'May': 'May.',
      'June': 'Jun.',
      'July': 'Jul.',
      'August': 'Aug.',
      'September': 'Sep.',
      'October': 'Oct.',
      'November': 'Nov.',
      'December': 'Dec.'
    };
    
    const abbreviated = monthAbbreviations[month] || month;
    const yearSuffix = year.length >= 2 ? year.slice(-2) : year;
    return `${abbreviated} '${yearSuffix}`;
  };

  // Handle error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-500" />
            ASIN's Results by Month
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-600 text-sm">
            Error loading data: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle no product selected
  if (!selectedProduct && !isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">ASIN's Results by Month</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            Select a product to view monthly results.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Handle no data available
  if (!data && !isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold">ASIN's Results by Month</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">
            No monthly results data available for this ASIN.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">ASIN's Results by Month</CardTitle>
        {selectedProduct && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing monthly performance metrics for {selectedProduct.id}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-4">
        {/* True spreadsheet-like table with unified rows */}
        <div className="w-full border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
          <div className="h-[600px] overflow-auto">
            <table className="w-full border-collapse">
              {/* Header row */}
              <thead className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="sticky left-0 z-30 bg-gray-100 dark:bg-gray-700 px-4 py-3 text-left font-bold text-gray-900 dark:text-gray-100 border-r border-b min-w-[200px]">
                    Metric
                  </th>
                  {/* TTM columns */}
                  {ttmColumns.map((ttmColumn) => (
                    <th key={ttmColumn} className="px-3 py-3 text-center font-bold text-gray-900 dark:text-gray-100 border-r border-b min-w-[120px] whitespace-nowrap">
                      {ttmColumn}
                    </th>
                  ))}
                  {/* Month columns */}
                  {sortedMonths.map((month) => (
                    <th key={month} className="px-3 py-3 text-center font-bold text-gray-900 dark:text-gray-100 border-r border-b min-w-[120px] whitespace-nowrap">
                      {abbreviateMonth(month)}
                    </th>
                  ))}
                  {/* Loading headers */}
                  {isLoading && ttmColumns.length === 0 && sortedMonths.length === 0 && (
                    <>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <th key={`loading-header-${i}`} className="px-3 py-3 text-center border-r border-b min-w-[120px]">
                          <Skeleton className="h-4 w-20 mx-auto" />
                        </th>
                      ))}
                    </>
                  )}
                </tr>
              </thead>
              
              {/* Data rows */}
              <tbody>
                {METRIC_CONFIGS.map((metricConfig, rowIndex) => (
                  <tr key={metricConfig.key} className={`hover:bg-gray-100 dark:hover:bg-gray-600 ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}`}>
                    {/* Metric name cell - sticky */}
                    <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-gray-900 dark:text-gray-100 border-r border-b min-w-[200px]">
                      {metricConfig.label}
                    </td>
                    
                    {/* TTM data cells */}
                    {ttmColumns.map((ttmColumn) => {
                      const ttmData = data?.monthlyMetrics?.[ttmColumn] as MonthlyMetric | undefined;
                      const value = ttmData?.[metricConfig.key];
                      
                      return (
                        <td key={`${metricConfig.key}-${ttmColumn}`} className="px-3 py-3 text-right border-r border-b min-w-[120px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-16 ml-auto" />
                          ) : (
                            formatValue(value, metricConfig.formatter)
                          )}
                        </td>
                      );
                    })}
                    
                    {/* Month data cells */}
                    {sortedMonths.map((month) => {
                      const monthData = data?.monthlyMetrics?.[month] as MonthlyMetric | undefined;
                      const value = monthData?.[metricConfig.key];
                      
                      return (
                        <td key={`${metricConfig.key}-${month}`} className="px-3 py-3 text-right border-r border-b min-w-[120px] whitespace-nowrap">
                          {isLoading ? (
                            <Skeleton className="h-4 w-16 ml-auto" />
                          ) : (
                            formatValue(value, metricConfig.formatter)
                          )}
                        </td>
                      );
                    })}
                    
                    {/* Loading data cells */}
                    {isLoading && ttmColumns.length === 0 && sortedMonths.length === 0 && (
                      <>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <td key={`loading-data-${metricConfig.key}-${i}`} className="px-3 py-3 text-right border-r border-b min-w-[120px]">
                            <Skeleton className="h-4 w-16 ml-auto" />
                          </td>
                        ))}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer information */}
        {data && (ttmColumns.length > 0 || sortedMonths.length > 0) && (
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Showing {ttmColumns.length > 0 ? `${ttmColumns.length} TTM columns and ` : ''}{sortedMonths.length} months of data • Last updated: {new Date().toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AsinResultsByMonthDashboard;